import { createClient } from '@supabase/supabase-js';
import { createCrossSubdomainAuthStorage } from '@rehub/supabase/auth-storage';
import { getAdminRole as getAdminRoleShared, checkAdminStatus as checkAdminStatusShared } from '@rehub/admin';
import { 
    GeneratedImage, CreditTransaction, PromoCode, PromoRedemption, AdminAction, RedeemPromoResult,
    EventLog, EventType, UserWithStatus, UserTimeline, IPAnalytics, DomainAnalytics,
    UserRiskStatus, ControlStatus, DomainControlStatus, RateLimitCheck,
    Generation, GenerationStatus, CreditLedgerEntry, GenerationsFilters, SalesMetrics, SalesTransaction,
    UserPaymentRow, UserCostSummary, UserResource,
    ShareDemo, ShareDemoEvent, ShareDemoPublic, ShareDemoAnalyticsSummary, ShareDemoEventType,
    SignupMetrics, SignupStatsOptions
} from '../types';
import { attributionPayload, currentFirstTouch } from '../lib/attribution';

// Explicitly access environment variables to ensure Vite's static replacement works correctly.
// Dynamic access (e.g. env[key]) often fails in production builds because the bundler cannot tree-shake or inline the values.

const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || process.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || process.env.VITE_SUPABASE_ANON_KEY || '';

const isConfigured = !!(supabaseUrl && supabaseAnonKey && supabaseUrl.startsWith('http') && !supabaseUrl.includes('placeholder'));

if (!isConfigured) {
  console.warn(
    'Supabase environment variables missing or invalid. Authentication will be disabled.\n' +
    'Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your environment (e.g. .env.local or Vercel Settings).'
  );
}

const validUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co';
const validKey = isConfigured ? supabaseAnonKey : 'placeholder';

export const supabase = createClient(validUrl, validKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: createCrossSubdomainAuthStorage(),
  },
  global: {
    headers: {},
  },
});

// Stripe billing portal (invoices, payment methods, subscriptions). Shared
// across every ReHub app via @rehub/commerce + the single Supabase project.
import { openBillingPortal as openBillingPortalShared } from '@rehub/commerce';
export const openBillingPortal = (returnUrl?: string) =>
  openBillingPortalShared(supabase, returnUrl);

// --- CLIENT IP TRACKING ---

let cachedClientIP: string | null = null;
let ipFetchPromise: Promise<string | null> | null = null;

/**
 * Fetch the client's public IP address via Edge Function
 * Caches the result for subsequent calls
 */
export const fetchClientIP = async (): Promise<string | null> => {
    if (!isConfigured) return null;
    
    // Return cached IP if available
    if (cachedClientIP) return cachedClientIP;
    
    // Return existing promise if fetch is in progress
    if (ipFetchPromise) return ipFetchPromise;
    
    // Start fetch
    ipFetchPromise = (async () => {
        try {
            const response = await fetch(`${supabaseUrl}/functions/v1/get-client-ip`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                signal: AbortSignal.timeout(4000),
            });
            
            if (response.ok) {
                const data = await response.json();
                cachedClientIP = data.ip || null;
                return cachedClientIP;
            }
        } catch (err) {
            console.warn('Failed to fetch client IP:', err);
        } finally {
            ipFetchPromise = null;
        }
        return null;
    })();
    
    return ipFetchPromise;
};

/**
 * Ask the server to drop a repeat welcome grant for this signed-in user.
 * The IP is taken from the request, not from the browser.
 * Returns the balance after the check, or null if the check could not run.
 */
export const applyWelcomeIpGuard = async (): Promise<number | null> => {
    if (!isConfigured) return null;

    try {
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return null;

        const response = await fetch(`${supabaseUrl}/functions/v1/get-client-ip`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                apikey: supabaseAnonKey,
                Authorization: `Bearer ${token}`,
            },
            signal: AbortSignal.timeout(4000),
        });
        if (!response.ok) return null;

        const data = await response.json();
        if (data?.ip) cachedClientIP = data.ip;
        if (typeof data?.welcome?.credits === 'number') return data.welcome.credits;
        return null;
    } catch (err) {
        console.warn('Welcome IP check failed:', err);
        return null;
    }
};

/**
 * Get the cached client IP (non-blocking)
 * Returns null if IP hasn't been fetched yet
 */
export const getClientIP = (): string | null => cachedClientIP;

/**
 * Log an event with IP capture via Edge Function
 * This routes through the server to capture the real client IP
 */
export const logEventWithIP = async (
    eventType: EventType,
    userId?: string | null,
    userEmail?: string | null,
    metadata?: Record<string, any>
): Promise<void> => {
    if (!isConfigured) return;
    
    try {
        await fetch(`${supabaseUrl}/functions/v1/get-client-ip`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                eventType,
                userId: userId || null,
                userEmail: userEmail || null,
                metadata: metadata || {}
            }),
        });
    } catch (err) {
        console.warn('Failed to log event with IP:', err);
    }
};

// --- HELPER FUNCTIONS ---

const base64ToBlob = (base64: string): Blob => {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);
  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }
  return new Blob([uInt8Array], { type: contentType });
};

export const uploadImageToStorage = async (
  userId: string, 
  imageData: File | Blob | string, 
  type: 'original' | 'processed' | 'output',
  customId?: string
): Promise<string | null> => {
    if (!isConfigured) return null;
    
    try {
        let blob: Blob;
        let ext = 'jpg';
        
        if (imageData instanceof File) {
             blob = imageData;
             ext = imageData.name.split('.').pop() || 'jpg';
        } else if (typeof imageData === 'string') {
             blob = base64ToBlob(imageData);
             if (imageData.startsWith('data:image/png')) ext = 'png';
             else if (imageData.startsWith('data:image/webp')) ext = 'webp';
        } else {
             blob = imageData;
        }

        const uniqueId = customId || crypto.randomUUID();
        const folder = type === 'original' ? 'originals' : type === 'output' ? 'outputs' : 'results';
        const filename = `${userId}/${folder}/${uniqueId}.${ext}`;

        // 3. Upload to 'images' bucket with Upsert: true
        const { data, error } = await supabase.storage
            .from('images')
            .upload(filename, blob, {
                cacheControl: '3600',
                upsert: true
            });

        if (error) {
            console.error('Storage Upload Error:', error);
            throw error;
        }

        // 4. Construct specific Public URL format dynamically
        const { data: { publicUrl } } = supabase.storage
            .from('images')
            .getPublicUrl(filename);
            
        return publicUrl;
    } catch (err) {
        console.error("Failed to upload image:", err);
        throw err;
    }
};

export const getUserImages = async (userId: string): Promise<GeneratedImage[]> => {
    if (!isConfigured) return [];
    
    const { data, error } = await supabase
        .from('images')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
        
    if (error) {
        console.error("Fetch Images Error:", error);
        return [];
    }
    return data as GeneratedImage[];
};

export const getUserTransactions = async (userId: string): Promise<CreditTransaction[]> => {
    if (!isConfigured) return [];
    
    const { data, error } = await supabase
        .from('credit_transactions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(100);
        
    if (error) {
        console.error("Fetch Transactions Error:", error);
        return [];
    }
    return data as CreditTransaction[];
};

export const refundCredit = async (userId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    try {
        const { error } = await supabase.rpc('refund_credit', { user_id: userId });
        if (error) {
            console.error("Refund credit failed:", error.message, error);
            return false;
        }
        console.log("Credit refunded successfully.");
        return true;
    } catch (err) {
        console.error("Refund credit exception:", err);
        return false;
    }
};

/**
 * Fallback: refund via edge function when RPC fails (e.g. refund_credit not in DB yet).
 * Uses the current session so the edge function can verify the user.
 */
export const refundCreditViaEdgeFunction = async (userId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    try {
        const { data, error } = await supabase.functions.invoke('refund-credit', {
            body: { userId },
        });
        if (error) {
            console.error("Refund (edge function) failed:", error);
            return false;
        }
        if (data?.success) {
            console.log("Credit refunded via edge function.");
            return true;
        }
        return false;
    } catch (err) {
        console.error("Refund (edge function) exception:", err);
        return false;
    }
};

/**
 * Store pending marketing consent before email confirmation
 * This is picked up by the handle_new_user trigger when user confirms
 */
export const storePendingMarketingConsent = async (email: string): Promise<void> => {
    if (!isConfigured) return;
    try {
        await supabase
            .from('pending_marketing_consent')
            .upsert({ email }, { onConflict: 'email' });
    } catch (err) {
        console.error('Failed to store pending marketing consent:', err);
    }
};

/**
 * Process image using server-side edge function
 * Handles generation tracking, credit consumption, and refunds
 */
export const processImageServerSide = async (
    imageBase64: string,
    prompt: string,
    aspectRatio: string = '1:1',
    action: 'declutter' | 'upscale' = 'declutter',
    userId?: string,
    userEmail?: string
): Promise<string> => {
    if (!isConfigured) {
        throw new Error('Supabase not configured');
    }

    const { data, error } = await supabase.functions.invoke('process-image', {
        body: {
            imageBase64,
            prompt,
            aspectRatio,
            action,
            userId,
            userEmail
        }
    });

    if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Server processing failed');
    }

    if (!data?.success || !data?.imageBase64) {
        throw new Error(data?.error || 'No image returned from server');
    }

    return data.imageBase64;
};

// =========================================
// PROMO CODE FUNCTIONS
// =========================================

export const redeemPromoCode = async (code: string): Promise<RedeemPromoResult> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_AUTHENTICATED' };
    }

    try {
        const { data, error } = await supabase.rpc('redeem_promo_code', {
            p_code: code
        });

        if (error) {
            console.error('Redeem promo error:', error);
            return { success: false, error: 'INVALID_CODE' };
        }

        return data as RedeemPromoResult;
    } catch (err) {
        console.error('Failed to redeem promo code:', err);
        return { success: false, error: 'INVALID_CODE' };
    }
};

export const getPromoCodes = async (): Promise<PromoCode[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

    if (error) {
        console.error('Fetch promo codes error:', error);
        return [];
    }

    return data as PromoCode[];
};

export const getPromoRedemptions = async (): Promise<PromoRedemption[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('promo_redemptions')
        .select('*')
        .order('redeemed_at', { ascending: false })
        .limit(100);

    if (error) {
        console.error('Fetch promo redemptions error:', error);
        return [];
    }

    return data as PromoRedemption[];
};

// =========================================
// ADMIN FUNCTIONS (shared @rehub/admin)
// =========================================

export const checkAdminStatus = async (userId: string): Promise<boolean> => {
    if (!isConfigured) return false;
    return checkAdminStatusShared(supabase, userId);
};

export type AdminRole = 'admin' | 'superadmin' | 'editor';
export const getAdminRole = async (userId: string): Promise<AdminRole | null> => {
    if (!isConfigured) return null;
    return getAdminRoleShared(supabase, userId);
};

// Editors & pro orders (shared with sketchplan)
export interface EditorRow { user_id: string; email: string | null; }
export interface DefaultEditorRow { user_id: string; position: number; email: string | null; }

export const getEditors = async (): Promise<EditorRow[]> => {
  if (!isConfigured) return [];
  const { data, error } = await supabase.rpc('admin_get_editors');
  if (error) { console.error('getEditors error:', error); return []; }
  return (data ?? []) as EditorRow[];
};

export const getDefaultEditors = async (): Promise<DefaultEditorRow[]> => {
  if (!isConfigured) return [];
  const { data, error } = await supabase.rpc('admin_get_default_editors');
  if (error) { console.error('getDefaultEditors error:', error); return []; }
  return (data ?? []) as DefaultEditorRow[];
};

export const addEditor = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { error } = await supabase.rpc('admin_add_editor', { p_user_id: userId });
  return error ? { success: false, error: error.message } : { success: true };
};

export const removeEditor = async (userId: string): Promise<{ success: boolean; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { error } = await supabase.rpc('admin_remove_editor', { p_user_id: userId });
  return error ? { success: false, error: error.message } : { success: true };
};

export const setDefaultEditors = async (userIds: string[]): Promise<{ success: boolean; error?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { error } = await supabase.rpc('admin_set_default_editors', { p_user_ids: userIds });
  return error ? { success: false, error: error.message } : { success: true };
};

export const createEditorUser = async (email: string, password?: string): Promise<{ success: boolean; error?: string; user_id?: string; password?: string }> => {
  if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
  const { data, error } = await supabase.functions.invoke('admin-create-editor', { body: { email: email.trim(), password: password || undefined } });
  if (error) return { success: false, error: error.message };
  if (data?.error) return { success: false, error: data.error };
  if (!data?.success) return { success: false, error: 'Unknown response' };
  return { success: true, user_id: data.user_id, password: data.password };
};

export const grantBonusCredits = async (
    targetUserId: string,
    amount: number,
    reason: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_grant_bonus_credits', {
            p_target_user_id: targetUserId,
            p_amount: amount,
            p_reason: reason
        });

        if (error) {
            console.error('Grant bonus credits error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to grant bonus credits:', err);
        return { success: false, error: err.message };
    }
};

export const createPromoCode = async (
    code: string,
    credits: number,
    maxUses: number | null = null,
    expiresAt: string | null = null,
    description: string | null = null
): Promise<{ success: boolean; error?: string; promo_id?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_create_promo_code', {
            p_code: code,
            p_credits: credits,
            p_max_uses: maxUses,
            p_expires_at: expiresAt,
            p_description: description
        });

        if (error) {
            console.error('Create promo code error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to create promo code:', err);
        return { success: false, error: err.message };
    }
};

export const updatePromoCode = async (
    promoId: string,
    active?: boolean,
    credits?: number | null,
    maxUses?: number | null,
    expiresAt?: string | null,
    description?: string | null
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) {
        return { success: false, error: 'NOT_CONFIGURED' };
    }

    try {
        const { data, error } = await supabase.rpc('admin_update_promo_code', {
            p_promo_id: promoId,
            p_active: active,
            p_credits: credits,
            p_max_uses: maxUses,
            p_expires_at: expiresAt,
            p_description: description
        });

        if (error) {
            console.error('Update promo code error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update promo code:', err);
        return { success: false, error: err.message };
    }
};

export const getAdminActions = async (): Promise<AdminAction[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('admin_actions')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

    if (error) {
        console.error('Fetch admin actions error:', error);
        return [];
    }

    return data as AdminAction[];
};

export const searchUsers = async (searchTerm: string): Promise<any[]> => {
    if (!isConfigured) return [];

    const { data, error } = await supabase
        .from('profiles')
        .select('id, email, credits')
        .ilike('email', `%${searchTerm}%`)
        .limit(20);

    if (error) {
        console.error('Search users error:', error);
        return [];
    }

    return data;
};

// =========================================
// BEHAVIOR MONITORING FUNCTIONS
// =========================================

/**
 * Log an event to the event_logs table
 * Automatically uses cached client IP if no IP is provided
 */
export const logEvent = async (
    eventType: EventType,
    userId?: string | null,
    userEmail?: string | null,
    ipAddress?: string | null,
    userAgent?: string | null,
    metadata?: Record<string, any>
): Promise<string | null> => {
    if (!isConfigured) return null;

    // Use cached IP if no IP provided
    const finalIP = ipAddress || cachedClientIP;

    try {
        const { data, error } = await supabase.rpc('log_event', {
            p_event_type: eventType,
            p_user_id: userId || null,
            p_user_email: userEmail || null,
            p_ip_address: finalIP || null,
            p_user_agent: userAgent || null,
            p_metadata: metadata || {}
        });

        if (error) {
            console.error('Log event error:', error);
            return null;
        }

        return data;
    } catch (err) {
        console.error('Failed to log event:', err);
        return null;
    }
};

/**
 * Get activity feed (admin only)
 */
export const getActivityFeed = async (
    limit: number = 100,
    offset: number = 0,
    eventType?: string,
    userId?: string,
    ipAddress?: string
): Promise<{ success: boolean; total?: number; events?: EventLog[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_activity_feed', {
            p_limit: limit,
            p_offset: offset,
            p_event_type: eventType || null,
            p_user_id: userId || null,
            p_ip_address: ipAddress || null
        });

        if (error) {
            console.error('Get activity feed error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get activity feed:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get users with their status (admin only)
 */
export const getUsersWithStatus = async (
    limit: number = 50,
    offset: number = 0,
    search?: string,
    statusFilter?: UserRiskStatus
): Promise<{ success: boolean; total?: number; users?: UserWithStatus[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_users_with_status', {
            p_limit: limit,
            p_offset: offset,
            p_search: search || null,
            p_status_filter: statusFilter || null
        });

        if (error) {
            console.error('Get users with status error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get users with status:', err);
        return { success: false, error: err.message };
    }
};

const emptySignupMetrics = (days: number, error: string): SignupMetrics => ({
    success: false, error, days,
    stats: { total: 0, google_ads: 0, today: 0, last_24h: 0, last_7d: 0, last_30d: 0, all_time: 0 },
    by_provider: {}, by_app: {}, per_day: [], signups: [],
});

/**
 * Get signup analytics for the admin Signups dashboard (admin only).
 * Returns totals, breakdown by auth provider and originating app, a per-day
 * series, and the raw signup rows for the selected window.
 */
export const getSignupStats = async (
    days: number = 30,
    limit: number = 5000,
    opts: SignupStatsOptions = {}
): Promise<SignupMetrics> => {
    if (!isConfigured) {
        return emptySignupMetrics(days, 'NOT_CONFIGURED');
    }

    try {
        const { data, error } = await supabase.rpc('admin_get_signups', {
            p_days: days,
            p_limit: limit,
            p_from: opts.from || null,
            p_to: opts.to || null,
            p_app: opts.app || null,
            p_provider: opts.provider || null,
        });

        if (error) {
            console.error('Get signup stats error:', error);
            return emptySignupMetrics(days, error.message);
        }

        return data as SignupMetrics;
    } catch (err: any) {
        console.error('Failed to get signup stats:', err);
        return emptySignupMetrics(days, err.message);
    }
};

/**
 * Write first-touch attribution for a newly created account.
 * The RPC ignores the call when a row already exists or the touch is from
 * a visit after the account was created.
 */
export const recordSignupAttributionIfPresent = async (): Promise<void> => {
    if (!isConfigured) return;
    const acquisition = attributionPayload(currentFirstTouch());
    if (!acquisition) return;

    try {
        const { error } = await supabase.rpc('record_signup_attribution', {
            p_acquisition: acquisition,
        });
        if (error) console.warn('Signup attribution was not stored:', error.message);
    } catch (err) {
        console.warn('Signup attribution was not stored:', err);
    }
};

/**
 * Earliest login IP per user (admin only). Used to map signup locations.
 */
export const getSignupIps = async (
    userIds: string[]
): Promise<{ success: boolean; ips: Record<string, string>; error?: string }> => {
    if (!isConfigured) return { success: false, ips: {}, error: 'NOT_CONFIGURED' };
    if (userIds.length === 0) return { success: true, ips: {} };

    try {
        const { data, error } = await supabase.rpc('admin_get_signup_ips', {
            p_user_ids: userIds,
        });

        if (error) {
            console.error('Get signup IPs error:', error);
            return { success: false, ips: {}, error: error.message };
        }

        const payload = data as { success?: boolean; ips?: Record<string, string>; error?: string };
        if (!payload?.success) {
            return { success: false, ips: {}, error: payload?.error || 'FAILED' };
        }
        return { success: true, ips: payload.ips || {} };
    } catch (err: any) {
        console.error('Failed to get signup IPs:', err);
        return { success: false, ips: {}, error: err.message };
    }
};

/**
 * Get user timeline (admin only)
 */
export const getUserTimeline = async (userId: string): Promise<{ success: boolean; user?: UserTimeline['user']; events?: EventLog[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_timeline', {
            p_user_id: userId
        });

        if (error) {
            console.error('Get user timeline error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user timeline:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update user status (admin only)
 */
export const updateUserStatus = async (
    userId: string,
    status: UserRiskStatus,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_user_status', {
            p_user_id: userId,
            p_status: status,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update user status error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update user status:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get IP analytics (admin only)
 */
export const getIPAnalytics = async (limit: number = 50, days: number = 30): Promise<{ success: boolean; ips?: IPAnalytics[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_ip_analytics', {
            p_limit: limit,
            p_days: days
        });

        if (error) {
            console.error('Get IP analytics error:', error);
            return { success: false, error: error.message };
        }

        // Database function returns { success, ips } directly
        return data as { success: boolean; ips?: IPAnalytics[]; error?: string };
    } catch (err: any) {
        console.error('Failed to get IP analytics:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update IP control (admin only)
 */
export const updateIPControl = async (
    ipAddress: string,
    status: ControlStatus,
    throttleMultiplier: number = 1.0,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_ip_control', {
            p_ip_address: ipAddress,
            p_status: status,
            p_throttle_multiplier: throttleMultiplier,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update IP control error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update IP control:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get domain analytics (admin only)
 */
export const getDomainAnalytics = async (limit: number = 50): Promise<{ success: boolean; domains?: DomainAnalytics[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_domain_analytics', {
            p_limit: limit
        });

        if (error) {
            console.error('Get domain analytics error:', error);
            return { success: false, error: error.message };
        }

        return { success: true, domains: data as DomainAnalytics[] };
    } catch (err: any) {
        console.error('Failed to get domain analytics:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Update domain control (admin only)
 */
export const updateDomainControl = async (
    domain: string,
    status: DomainControlStatus,
    throttleMultiplier: number = 1.0,
    reason?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_update_domain_control', {
            p_domain: domain,
            p_status: status,
            p_throttle_multiplier: throttleMultiplier,
            p_reason: reason || null
        });

        if (error) {
            console.error('Update domain control error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to update domain control:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Check rate limits for magic link / login
 */
export const checkRateLimits = async (
    email: string,
    ipAddress?: string
): Promise<RateLimitCheck> => {
    if (!isConfigured) return { allowed: true };

    try {
        const { data, error } = await supabase.rpc('check_rate_limits', {
            p_email: email,
            p_ip_address: ipAddress || null
        });

        if (error) {
            console.error('Check rate limits error:', error);
            // Fail open - allow if check fails
            return { allowed: true };
        }

        return data as RateLimitCheck;
    } catch (err) {
        console.error('Failed to check rate limits:', err);
        return { allowed: true };
    }
};

/**
 * Check user status for generation permission
 */
export const checkUserGenerationPermission = async (userId: string): Promise<{ allowed: boolean; reason?: string }> => {
    if (!isConfigured) return { allowed: true };

    try {
        const { data, error } = await supabase.rpc('check_user_status', {
            p_user_id: userId
        });

        if (error) {
            console.error('Check user status error:', error);
            return { allowed: true };
        }

        return {
            allowed: data.allowed,
            reason: data.reason
        };
    } catch (err) {
        console.error('Failed to check user status:', err);
        return { allowed: true };
    }
};

// =========================================
// GENERATIONS & CREDIT LEDGER FUNCTIONS
// =========================================

/**
 * Create a generation record (for tracking)
 * Automatically uses cached client IP
 */
export const createGenerationRecord = async (
    userId?: string,
    userEmail?: string,
    metadata?: Record<string, any>
): Promise<string | null> => {
    if (!isConfigured) return null;

    try {
        const { data, error } = await supabase.rpc('create_generation', {
            p_user_id: userId || null,
            p_user_email: userEmail || null,
            p_input_asset_path: null,
            p_ip_address: cachedClientIP || null,
            p_user_agent: navigator?.userAgent || null,
            p_metadata: metadata || {}
        });

        if (error) {
            console.error('Create generation error:', error);
            return null;
        }

        return data;
    } catch (err) {
        console.error('Failed to create generation:', err);
        return null;
    }
};

/**
 * Update a generation record status
 */
export const updateGenerationRecord = async (
    generationId: string,
    status: 'completed' | 'failed',
    creditsUsed?: number,
    errorMessage?: string,
    outputUrl?: string,
    inputUrl?: string
): Promise<void> => {
    if (!isConfigured) return;

    try {
        await supabase.rpc('update_generation_status', {
            p_generation_id: generationId,
            p_status: status,
            p_output_path: outputUrl || null,
            p_credits_used: creditsUsed || 0,
            p_error_message: errorMessage || null,
            p_metadata: null,
            p_input_path: inputUrl || null
        });
    } catch (err) {
        console.error('Failed to update generation:', err);
    }
};

/**
 * Get paginated list of generations (admin only)
 */
export const getGenerations = async (
    filters: GenerationsFilters = {}
): Promise<{ success: boolean; total?: number; generations?: Generation[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_generations', {
            p_limit: filters.limit || 50,
            p_offset: filters.offset || 0,
            p_user_id: filters.userId || null,
            p_user_email: filters.userEmail || null,
            p_status: filters.status || null,
            p_date_from: filters.dateFrom || null,
            p_date_to: filters.dateTo || null
        });

        if (error) {
            console.error('Get generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user's generations (admin only)
 */
export const getUserGenerations = async (
    userId: string,
    limit: number = 20
): Promise<{ success: boolean; generations?: Generation[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_generations', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get all resources a user created across ReHub apps (admin only).
 * Includes generations, ImageMotion, tours, brochures, showcase, sketchplan orders, VideoMotion, Record.
 */
export const getUserResources = async (
    userId: string,
    limit: number = 50
): Promise<{ success: boolean; resources?: UserResource[]; total?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_resources', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user resources error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user resources:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user's credit ledger (admin only)
 */
export const getUserCreditLedger = async (
    userId: string,
    limit: number = 50
): Promise<{ success: boolean; balance?: number; total?: number; entries?: CreditLedgerEntry[]; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_credit_ledger', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user credit ledger error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user credit ledger:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get user payments / purchases (admin only) - from credit_transactions (type purchase, bonus, refund)
 */
export const getUserPayments = async (
    userId: string,
    limit: number = 50
): Promise<{ success: boolean; payments?: UserPaymentRow[]; summary?: UserCostSummary; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_user_payments', {
            p_user_id: userId,
            p_limit: limit
        });

        if (error) {
            console.error('Get user payments error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to get user payments:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Adjust user credits (admin only) - uses immutable ledger
 */
export const adjustUserCredits = async (
    userId: string,
    delta: number,
    reason: string
): Promise<{ success: boolean; new_balance?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_adjust_credits', {
            p_user_id: userId,
            p_delta: delta,
            p_reason: reason
        });

        if (error) {
            console.error('Adjust user credits error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to adjust user credits:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get image URL from storage path
 * For public bucket images, returns public URL
 * For private bucket images, would need signed URL (handled server-side)
 */
export const getImageUrl = (storagePath: string | null): string | null => {
    if (!storagePath) return null;
    
    // If it's already a full URL, return as-is
    if (storagePath.startsWith('http')) {
        return storagePath;
    }
    
    // Assume public bucket 'images'
    const { data } = supabase.storage
        .from('images')
        .getPublicUrl(storagePath);
    
    return data.publicUrl;
};

const IMAGES_PATH_MARKERS = [
    '/object/public/images/',
    '/object/sign/images/',
    '/render/image/public/images/',
    '/render/image/sign/images/',
    '/images/',
] as const;

/** Pull the object key out of a stored public/signed URL, or return a raw path as-is. */
export const extractImagesStoragePath = (urlOrPath: string | null | undefined): string | null => {
    if (!urlOrPath) return null;
    const trimmed = urlOrPath.trim();
    if (!trimmed) return null;
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
        return trimmed.replace(/^\/+/, '');
    }
    try {
        const url = new URL(trimmed);
        for (const marker of IMAGES_PATH_MARKERS) {
            const idx = url.pathname.indexOf(marker);
            if (idx !== -1) {
                const path = decodeURIComponent(url.pathname.slice(idx + marker.length));
                return path || null;
            }
        }
    } catch {
        return null;
    }
    return null;
};

export type StorageImageTransform = {
    width?: number;
    height?: number;
    quality?: number;
    resize?: 'cover' | 'contain' | 'fill';
};

/**
 * Mint a fetchable URL for an images-bucket object. Prefers a signed URL (works
 * for admin viewing another user's files) and only uses a public URL as fallback.
 * Image transforms are optional — they require a paid Supabase plan.
 */
export const resolveStorageImageUrl = async (
    urlOrPath: string | null | undefined,
    transform?: StorageImageTransform
): Promise<string | null> => {
    if (!urlOrPath) return null;
    const fallback = urlOrPath.startsWith('http') ? urlOrPath : getImageUrl(urlOrPath);
    const path = extractImagesStoragePath(urlOrPath);
    if (!isConfigured || !path) return fallback;

    const sign = async (withTransform: boolean) => {
        const { data, error } = await supabase.storage.from('images').createSignedUrl(
            path,
            60 * 60,
            withTransform && transform?.width
                ? {
                    transform: {
                        width: transform.width,
                        height: transform.height,
                        quality: transform.quality,
                        resize: transform.resize,
                    },
                }
                : undefined
        );
        if (error || !data?.signedUrl) return null;
        return data.signedUrl;
    };

    try {
        const signed = await sign(!!transform);
        if (signed) return signed;
        if (transform) {
            const plain = await sign(false);
            if (plain) return plain;
        }
    } catch (err) {
        console.warn('resolveStorageImageUrl failed, using fallback:', err);
    }
    return fallback;
};

/**
 * Thumbnail URL via Supabase Image Transformations (on-the-fly resize, no pre-created files).
 * Use for grids/lists; keep getImageUrl for download/full-size links.
 * Requires Supabase Pro for /render/image/ transforms.
 */
export const getThumbnailUrl = (
    imageUrl: string | null | undefined,
    options: { width?: number; height?: number; quality?: number; resize?: 'cover' | 'contain' | 'fill' } = {}
): string | null => {
    if (!imageUrl) return null;
    const { width = 400, height = 400, quality = 75, resize = 'cover' } = options;
    const params = new URLSearchParams();
    if (width) params.append('width', width.toString());
    if (height) params.append('height', height.toString());
    if (quality) params.append('quality', quality.toString());
    if (resize) params.append('resize', resize);
    const transformedUrl = imageUrl.includes('/storage/v1/object/')
        ? imageUrl.replace('/storage/v1/object/', '/storage/v1/render/image/')
        : imageUrl;
    const separator = transformedUrl.includes('?') ? '&' : '?';
    return `${transformedUrl}${separator}${params.toString()}`;
};

/**
 * Get signed URL for private image (admin only)
 * Note: This must be called from an edge function for security
 */
export const getSignedImageUrl = async (
    bucket: string,
    path: string,
    expiresIn: number = 3600
): Promise<{ success: boolean; url?: string; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.storage
            .from(bucket)
            .createSignedUrl(path, expiresIn);

        if (error) {
            console.error('Get signed URL error:', error);
            return { success: false, error: error.message };
        }

        return { success: true, url: data.signedUrl };
    } catch (err: any) {
        console.error('Failed to get signed URL:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Admin: Cleanup stuck generations (admin only)
 * Marks generations stuck in 'started' for >threshold minutes as 'failed' and refunds credits
 */
export const cleanupStuckGenerations = async (
    thresholdMinutes: number = 15
): Promise<{ success: boolean; generations_cleaned?: number; credits_refunded?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_cleanup_stuck_generations', {
            p_threshold_minutes: thresholdMinutes
        });

        if (error) {
            console.error('Cleanup stuck generations error:', error);
            return { success: false, error: error.message };
        }

        return data;
    } catch (err: any) {
        console.error('Failed to cleanup stuck generations:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Get count of stuck generations (for admin display)
 */
export const getStuckGenerationsCount = async (
    thresholdMinutes: number = 15
): Promise<number> => {
    if (!isConfigured) return 0;

    try {
        const thresholdTime = new Date(Date.now() - thresholdMinutes * 60 * 1000).toISOString();
        const { count, error } = await supabase
            .from('generations')
            .select('*', { count: 'exact', head: true })
            .eq('status', 'started')
            .lt('created_at', thresholdTime);

        if (error) {
            console.error('Get stuck generations count error:', error);
            return 0;
        }

        return count || 0;
    } catch (err) {
        console.error('Failed to get stuck generations count:', err);
        return 0;
    }
};

/**
 * Admin: Get sales metrics (admin only)
 * Returns profitability metrics for the given date range
 */
export const getSalesMetrics = async (
    dateFrom?: string,
    dateTo?: string
): Promise<{ success: boolean; metrics?: SalesMetrics; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_sales_metrics', {
            p_date_from: dateFrom || null,
            p_date_to: dateTo || null
        });

        if (error) {
            console.error('Get sales metrics error:', error);
            return { success: false, error: error.message };
        }

        if (!data?.success) {
            return { success: false, error: data?.error || 'Unknown error' };
        }

        return { success: true, metrics: data.metrics };
    } catch (err: any) {
        console.error('Failed to get sales metrics:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Admin: List purchase transactions that make up Sales & Costs revenue.
 */
export const getSalesTransactions = async (
    dateFrom?: string,
    dateTo?: string,
    limit: number = 50,
    offset: number = 0,
    search?: string
): Promise<{ success: boolean; transactions?: SalesTransaction[]; total?: number; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    try {
        const { data, error } = await supabase.rpc('admin_get_sales_transactions', {
            p_date_from: dateFrom || null,
            p_date_to: dateTo || null,
            p_limit: limit,
            p_offset: offset,
            p_search: search?.trim() || null,
        });

        if (error) {
            console.error('Get sales transactions error:', error);
            return { success: false, error: error.message };
        }

        if (!data?.success) {
            return { success: false, error: data?.error || 'Unknown error' };
        }

        return {
            success: true,
            transactions: data.transactions || [],
            total: data.total || 0,
        };
    } catch (err: any) {
        console.error('Failed to get sales transactions:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Admin: Record a manual sale by customer email (e.g. bank transfer) so it appears in Sales & Costs.
 * Does not add credits - use when you already granted credits and want to log the sale for reporting.
 */
export const recordManualSaleByEmail = async (
    email: string,
    amount: number,
    description?: string
): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };
    try {
        const { data, error } = await supabase.rpc('admin_record_manual_sale_by_email', {
            p_email: email.trim(),
            p_amount: amount,
            p_description: description || 'Manual sale',
        });
        if (error) return { success: false, error: error.message };
        if (!data?.success) return { success: false, error: data?.error || 'Unknown error' };
        return { success: true };
    } catch (err: any) {
        console.error('Record manual sale error:', err);
        return { success: false, error: err.message };
    }
};

// =========================================
// ADMIN: IMAGEMOTION PROJECTS
// =========================================

export interface AdminImageMotionProject {
    id: string;
    name: string;
    status: string;
    quality: string;
    created_at: string;
    updated_at: string;
    user_id: string | null;
    owner_email: string | null;
    image_count: number;
    share_token: string | null;
    is_promoted: boolean;
    final_video_url: string | null;
    final_video_path: string | null;
}

export const getAdminImageMotionProjects = async (): Promise<AdminImageMotionProject[]> => {
    if (!isConfigured) return [];

    const { data: projects, error } = await supabase
        .from('imagemotion_projects')
        .select('id, name, status, quality, created_at, updated_at, user_id, share_token, is_promoted, final_video_url, final_video_path')
        .order('created_at', { ascending: false });

    if (error) {
        console.error('getAdminImageMotionProjects error:', error);
        return [];
    }
    if (!projects?.length) return [];

    const userIds = [...new Set(
        (projects as { user_id: string | null }[])
            .map((p) => p.user_id)
            .filter((id): id is string => !!id)
    )];
    const projectIds = (projects as { id: string }[]).map((p) => p.id);

    const [{ data: profiles, error: profilesError }, { data: images }] = await Promise.all([
        userIds.length > 0
            ? supabase.from('profiles').select('id, email').in('id', userIds)
            : Promise.resolve({ data: [], error: null }),
        supabase.from('imagemotion_project_images').select('project_id').in('project_id', projectIds),
    ]);
    if (profilesError) console.error('getAdminImageMotionProjects profiles error:', profilesError);

    const emailByUserId = new Map<string, string | null>();
    for (const p of profiles || []) {
        emailByUserId.set((p as { id: string; email?: string }).id, (p as { email?: string }).email ?? null);
    }

    const imageCountByProject = new Map<string, number>();
    for (const img of images || []) {
        const pid = (img as { project_id: string }).project_id;
        imageCountByProject.set(pid, (imageCountByProject.get(pid) ?? 0) + 1);
    }

    return (projects as Array<Record<string, unknown>>).map((p) => ({
        id: p.id as string,
        name: (p.name as string) || 'Untitled',
        status: p.status as string,
        quality: (p.quality as string) || 'standard',
        created_at: p.created_at as string,
        updated_at: p.updated_at as string,
        user_id: (p.user_id as string) || null,
        owner_email: p.user_id ? emailByUserId.get(p.user_id as string) ?? null : null,
        image_count: imageCountByProject.get(p.id as string) ?? 0,
        share_token: (p.share_token as string) ?? null,
        is_promoted: !!(p.is_promoted as boolean),
        final_video_url: (p.final_video_url as string) ?? null,
        final_video_path: (p.final_video_path as string) ?? null,
    }));
};

export const getAdminVideoSignedUrl = async (storagePath: string): Promise<string | null> => {
    if (!isConfigured) return null;
    const { data, error } = await supabase.storage
        .from('imagemotion-uploads')
        .createSignedUrl(storagePath, 3600);
    if (error) {
        console.error('getAdminVideoSignedUrl error:', error);
        return null;
    }
    return data.signedUrl;
};

const IMAGEMOTION_UPLOADS_BUCKET = 'imagemotion-uploads';

/** Object key inside imagemotion-uploads, from a storage path or a public/signed URL. */
export const imagemotionObjectKey = (pathOrUrl: string | null | undefined): string | null => {
    if (!pathOrUrl) return null;
    const trimmed = pathOrUrl.trim();
    if (!trimmed) return null;
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
        return trimmed.replace(/^\/+/, '');
    }
    try {
        const url = new URL(trimmed);
        const markers = [
            '/object/public/imagemotion-uploads/',
            '/object/sign/imagemotion-uploads/',
        ];
        for (const marker of markers) {
            const idx = url.pathname.indexOf(marker);
            if (idx !== -1) {
                const path = decodeURIComponent(url.pathname.slice(idx + marker.length));
                return path || null;
            }
        }
    } catch {
        return null;
    }
    return null;
};

/** Public playback URL. The bucket is public, so this plays inline (signed URLs often download). */
export const getAdminImageMotionPublicUrl = (pathOrUrl: string | null | undefined): string | null => {
    const key = imagemotionObjectKey(pathOrUrl);
    if (!key) return null;
    const { data } = supabase.storage.from(IMAGEMOTION_UPLOADS_BUCKET).getPublicUrl(key);
    return data.publicUrl || null;
};

export type AdminVideoItem = { label: string; url: string };

/**
 * Final render plus per-image clips for an ImageMotion project.
 * Admins can read these rows; playback uses the public bucket URL.
 */
export const getAdminImageMotionPlayback = async (
    projectId: string,
    finalPath: string | null,
    finalUrl: string | null,
): Promise<AdminVideoItem[]> => {
    const items: AdminVideoItem[] = [];
    const final = getAdminImageMotionPublicUrl(finalPath) || getAdminImageMotionPublicUrl(finalUrl);
    if (final) items.push({ label: 'Final video', url: final });

    const { data, error } = await supabase
        .from('imagemotion_project_images')
        .select('id, clip_path, clip_url')
        .eq('project_id', projectId)
        .order('order_index', { ascending: true });

    if (error) {
        console.error('getAdminImageMotionPlayback clips error:', error);
        return items;
    }

    (data || []).forEach((row, index) => {
        const clip = getAdminImageMotionPublicUrl(row.clip_path) || getAdminImageMotionPublicUrl(row.clip_url);
        if (clip) items.push({ label: `Clip ${index + 1}`, url: clip });
    });

    return items;
};

// =========================================
// ADMIN: 360 TOURS
// =========================================

export interface AdminTourRow {
    id: string;
    title: string;
    slug: string;
    status: 'draft' | 'published' | 'archived';
    hosting_status: string;
    hosting_plan: string | null;
    hosting_expires_at: string | null;
    view_count: number;
    scene_count: number;
    created_at: string;
    user_id: string;
    owner_email: string | null;
    build_mode: string | null;
}

export const getAdminTours = async (): Promise<AdminTourRow[]> => {
    if (!isConfigured) return [];

    const { data: tours, error } = await supabase
        .from('tours')
        .select('id, title, slug, status, hosting_status, hosting_plan, hosting_expires_at, view_count, created_at, user_id, build_mode')
        .order('created_at', { ascending: false });

    if (error) {
        console.error('getAdminTours error:', error);
        return [];
    }
    if (!tours?.length) return [];

    const userIds = [...new Set((tours as { user_id: string }[]).map((t) => t.user_id))];
    const tourIds = (tours as { id: string }[]).map((t) => t.id);

    const [{ data: profiles }, { data: scenes }] = await Promise.all([
        supabase.from('profiles').select('id, email').in('id', userIds),
        supabase.from('scenes').select('tour_id').in('tour_id', tourIds),
    ]);

    const emailByUserId = new Map<string, string | null>();
    for (const p of profiles || []) {
        emailByUserId.set((p as { id: string; email?: string }).id, (p as { email?: string }).email ?? null);
    }

    const sceneCountByTour = new Map<string, number>();
    for (const s of scenes || []) {
        const tid = (s as { tour_id: string }).tour_id;
        sceneCountByTour.set(tid, (sceneCountByTour.get(tid) ?? 0) + 1);
    }

    return (tours as Array<Record<string, unknown>>).map((t) => ({
        id: t.id as string,
        title: (t.title as string) || 'Untitled',
        slug: t.slug as string,
        status: t.status as AdminTourRow['status'],
        hosting_status: (t.hosting_status as string) ?? 'inactive',
        hosting_plan: (t.hosting_plan as string) ?? null,
        hosting_expires_at: (t.hosting_expires_at as string) ?? null,
        view_count: (t.view_count as number) ?? 0,
        scene_count: sceneCountByTour.get(t.id as string) ?? 0,
        created_at: t.created_at as string,
        user_id: t.user_id as string,
        owner_email: emailByUserId.get(t.user_id as string) ?? null,
        build_mode: (t.build_mode as string) ?? null,
    }));
};

// =========================================
// SHARE DEMO FUNCTIONS
// =========================================

const toSlug = (text: string): string =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').substring(0, 60);

export const generateUniqueSlug = async (baseName: string): Promise<string> => {
    const base = toSlug(baseName);
    if (!base) return `demo-${Date.now().toString(36)}`;

    const { data } = await supabase
        .from('photoclear_share_demos')
        .select('slug')
        .like('slug', `${base}%`);

    const existing = new Set((data || []).map((r: { slug: string }) => r.slug));
    if (!existing.has(base)) return base;

    let i = 2;
    while (existing.has(`${base}-${i}`)) i++;
    return `${base}-${i}`;
};

export const createShareDemo = async (
    demo: Partial<ShareDemo> & { internal_name: string; original_image_url: string; created_by: string }
): Promise<{ success: boolean; demo?: ShareDemo; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    const slug = demo.slug || await generateUniqueSlug(demo.internal_name);

    const { data, error } = await supabase
        .from('photoclear_share_demos')
        .insert({ ...demo, slug })
        .select()
        .single();

    if (error) return { success: false, error: error.message };
    return { success: true, demo: data as ShareDemo };
};

export const updateShareDemo = async (
    id: string,
    updates: Partial<ShareDemo>
): Promise<{ success: boolean; demo?: ShareDemo; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    const { data, error } = await supabase
        .from('photoclear_share_demos')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

    if (error) return { success: false, error: error.message };
    return { success: true, demo: data as ShareDemo };
};

export const deleteShareDemo = async (id: string): Promise<{ success: boolean; error?: string }> => {
    if (!isConfigured) return { success: false, error: 'NOT_CONFIGURED' };

    const { error } = await supabase.from('photoclear_share_demos').delete().eq('id', id);
    if (error) return { success: false, error: error.message };
    return { success: true };
};

export const getShareDemos = async (
    statusFilter?: string,
    search?: string
): Promise<ShareDemo[]> => {
    if (!isConfigured) return [];

    let query = supabase
        .from('photoclear_share_demos')
        .select('*')
        .order('created_at', { ascending: false });

    if (statusFilter && statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
    }
    if (search) {
        query = query.or(`internal_name.ilike.%${search}%,slug.ilike.%${search}%,agency_name.ilike.%${search}%`);
    }

    const { data, error } = await query;
    if (error) {
        console.error('getShareDemos error:', error);
        return [];
    }
    return (data || []) as ShareDemo[];
};

export const getShareDemo = async (id: string): Promise<ShareDemo | null> => {
    if (!isConfigured) return null;

    const { data, error } = await supabase
        .from('photoclear_share_demos')
        .select('*')
        .eq('id', id)
        .single();

    if (error) return null;
    return data as ShareDemo;
};

export const getPublicShareDemo = async (slug: string): Promise<ShareDemoPublic | null> => {
    if (!isConfigured) return null;

    const { data, error } = await supabase.rpc('get_public_share_demo', { p_slug: slug });
    if (error || data?.error) return null;
    return data as ShareDemoPublic;
};

export const recordShareDemoEvent = async (
    slug: string,
    eventType: ShareDemoEventType,
    extra?: {
        visitor_id?: string;
        session_id?: string;
        email?: string;
        first_name?: string;
        agency_name?: string;
        metadata?: Record<string, any>;
    }
): Promise<boolean> => {
    if (!isConfigured) return false;
    try {
        const { data, error } = await supabase.rpc('record_share_demo_event', {
            p_slug: slug,
            p_event_type: eventType,
            p_visitor_id: extra?.visitor_id || null,
            p_session_id: extra?.session_id || null,
            p_email: extra?.email || null,
            p_first_name: extra?.first_name || null,
            p_agency_name: extra?.agency_name || null,
            p_user_agent: navigator?.userAgent || null,
            p_referrer: document?.referrer || null,
            p_ip_address: null,
            p_metadata: extra?.metadata || {},
        });
        return !error && data?.success;
    } catch {
        return false;
    }
};

export const getShareDemoAnalytics = async (demoId: string): Promise<ShareDemoAnalyticsSummary> => {
    const empty: ShareDemoAnalyticsSummary = {
        views: 0, unique_visitors: 0, slider_interactions: 0,
        download_clicks: 0, download_completes: 0, emails_captured: 0, cta_clicks: 0,
        recent_leads: [],
    };
    if (!isConfigured) return empty;

    const { data: events, error } = await supabase
        .from('photoclear_share_demo_events')
        .select('*')
        .eq('share_demo_id', demoId)
        .order('created_at', { ascending: false });

    if (error || !events) return empty;

    const typed = events as ShareDemoEvent[];
    const visitors = new Set(typed.filter(e => e.visitor_id).map(e => e.visitor_id));

    return {
        views: typed.filter(e => e.event_type === 'view').length,
        unique_visitors: visitors.size,
        slider_interactions: typed.filter(e => e.event_type === 'slider_interaction').length,
        download_clicks: typed.filter(e => e.event_type === 'download_click').length,
        download_completes: typed.filter(e => e.event_type === 'download_complete').length,
        emails_captured: typed.filter(e => e.event_type === 'email_capture').length,
        cta_clicks: typed.filter(e => e.event_type === 'cta_click').length,
        recent_leads: typed
            .filter(e => e.event_type === 'email_capture' && e.email)
            .slice(0, 20)
            .map(e => ({
                email: e.email!,
                first_name: e.first_name,
                agency_name: e.agency_name,
                created_at: e.created_at,
            })),
    };
};

export const uploadShareDemoImage = async (
    file: File | Blob | string,
    demoId: string,
    type: 'original' | 'processed' | 'agency_logo'
): Promise<string | null> => {
    if (!isConfigured) return null;

    let blob: Blob;
    let ext = 'jpg';

    if (file instanceof File) {
        blob = file;
        ext = file.name.split('.').pop() || 'jpg';
    } else if (typeof file === 'string') {
        blob = base64ToBlob(file);
        if (file.startsWith('data:image/png')) ext = 'png';
        else if (file.startsWith('data:image/webp')) ext = 'webp';
    } else {
        blob = file;
    }

    const filename = `share-demos/${demoId}/${type}.${ext}`;

    const { error } = await supabase.storage
        .from('images')
        .upload(filename, blob, { cacheControl: '31536000', upsert: true });

    if (error) {
        console.error('uploadShareDemoImage error:', error);
        return null;
    }

    const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filename);

    return publicUrl;
};

/**
 * Upload an admin-supplied video (produced outside the system) into the
 * public `images` bucket so the share-demo public page can play it directly.
 * Used by the manual "Upload Video" share-demo workflow.
 */
export const uploadShareDemoVideo = async (
    file: File | Blob,
    demoId: string
): Promise<string | null> => {
    if (!isConfigured) return null;

    const ext = file instanceof File ? (file.name.split('.').pop() || 'mp4') : 'mp4';
    const filename = `share-demos/${demoId}/processed_video.${ext}`;

    const { error } = await supabase.storage
        .from('images')
        .upload(filename, file, {
            cacheControl: '31536000',
            upsert: true,
            contentType: file.type || 'video/mp4',
        });

    if (error) {
        console.error('uploadShareDemoVideo error:', error);
        return null;
    }

    const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filename);

    return publicUrl;
};

// =========================================================================
// HYPER DUSK INTEGRATION (for share demos)
// Talks to the shared imagemotion_projects table + edge functions used by
// the Hyper Dusk app. Storage bucket is `imagemotion-uploads`.
// =========================================================================

import type {
    HyperDuskAnimationStyle,
    HyperDuskProjectImageLite,
    HyperDuskProjectLite,
    HyperDuskVariantLite,
} from '../hyperdusk-shared';

const HYPER_DUSK_BUCKET = 'imagemotion-uploads';
const HYPER_DUSK_SIGN_TTL_SECONDS = 60 * 60 * 24 * 7;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const extractHyperDuskStoragePath = (signedUrl: string): string | null => {
    try {
        const url = new URL(signedUrl);
        const marker = `/object/sign/${HYPER_DUSK_BUCKET}/`;
        const idx = url.pathname.indexOf(marker);
        if (idx === -1) return null;
        return decodeURIComponent(url.pathname.slice(idx + marker.length));
    } catch {
        return null;
    }
};

export const getFreshHyperDuskUrl = async (
    storagePath: string | null,
    fallbackUrl: string | null
): Promise<string | null> => {
    if (storagePath) {
        const { data, error } = await supabase.storage
            .from(HYPER_DUSK_BUCKET)
            .createSignedUrl(storagePath, HYPER_DUSK_SIGN_TTL_SECONDS);
        if (!error && data?.signedUrl) return data.signedUrl;
    }
    if (fallbackUrl) {
        const path = extractHyperDuskStoragePath(fallbackUrl);
        if (path) {
            const { data, error } = await supabase.storage
                .from(HYPER_DUSK_BUCKET)
                .createSignedUrl(path, HYPER_DUSK_SIGN_TTL_SECONDS);
            if (!error && data?.signedUrl) return data.signedUrl;
        }
    }
    return fallbackUrl;
};

export const uploadHyperDuskImage = async (
    file: File,
    userId: string
): Promise<{ url: string; path: string } | null> => {
    if (!isConfigured) return null;
    const ext = file.name.split('.').pop() || 'jpg';
    const storagePath = `images/${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;

    const { data, error } = await supabase.storage
        .from(HYPER_DUSK_BUCKET)
        .upload(storagePath, file, { cacheControl: '3600', upsert: false });
    if (error || !data?.path) {
        console.error('uploadHyperDuskImage upload error:', error);
        return null;
    }

    const { data: signed, error: signErr } = await supabase.storage
        .from(HYPER_DUSK_BUCKET)
        .createSignedUrl(data.path, HYPER_DUSK_SIGN_TTL_SECONDS);
    if (signErr || !signed?.signedUrl) {
        const { data: pub } = supabase.storage.from(HYPER_DUSK_BUCKET).getPublicUrl(data.path);
        if (pub?.publicUrl) return { url: pub.publicUrl, path: data.path };
        return null;
    }
    return { url: signed.signedUrl, path: data.path };
};

const normalizeHyperDuskVariants = (value: unknown): HyperDuskVariantLite[] => {
    if (!Array.isArray(value)) return [];
    return value
        .map((item) => {
            if (!item || typeof item !== 'object') return null;
            const raw = item as Record<string, unknown>;
            if (typeof raw.id !== 'string' || typeof raw.path !== 'string') return null;
            return {
                id: raw.id,
                path: raw.path,
                url: typeof raw.url === 'string' ? raw.url : null,
                created_at: typeof raw.created_at === 'string' ? raw.created_at : new Date().toISOString(),
                preset_id: typeof raw.preset_id === 'string' ? raw.preset_id : null,
                animation_style: typeof raw.animation_style === 'string'
                    ? (raw.animation_style as HyperDuskAnimationStyle)
                    : null,
            } as HyperDuskVariantLite;
        })
        .filter((v): v is HyperDuskVariantLite => !!v);
};

export const createHyperDuskShareProject = async (params: {
    userId: string;
    name: string;
    image: { url: string; path: string | null };
    aspectRatio: string;
    animationStyle: HyperDuskAnimationStyle;
    durationSeconds: number;
}): Promise<{ projectId: string; imageId: string } | null> => {
    if (!isConfigured) return null;

    // Insert project flagged as hyper_dusk; share-demo flow does not consume
    // user credits (admin tooling), so we skip the credit RPC.
    const { data: project, error: projectError } = await supabase
        .from('imagemotion_projects')
        .insert({
            user_id: params.userId,
            name: params.name || null,
            status: 'editing',
            quality: 'standard',
            service_type: 'hyper_dusk',
            transition_type: 'dissolve',
            settings: {
                transition_effect: 'dissolve',
                aspect_ratio: params.aspectRatio,
                output_resolution: 'hd',
                clip_duration: params.durationSeconds,
                share_demo: true,
            },
        })
        .select('id')
        .single();

    if (projectError || !project) {
        console.error('createHyperDuskShareProject project insert failed:', projectError);
        return null;
    }

    const { data: imageRow, error: imgError } = await supabase
        .from('imagemotion_project_images')
        .insert({
            project_id: project.id,
            image_url: params.image.url,
            image_path: params.image.path,
            source_image_url: params.image.url,
            source_image_path: params.image.path,
            order_index: 0,
            clip_duration: params.durationSeconds,
            kling_animation_style: params.animationStyle,
            generation_status: 'pending',
        })
        .select('id')
        .single();

    if (imgError || !imageRow) {
        console.error('createHyperDuskShareProject image insert failed:', imgError);
        return null;
    }

    return { projectId: project.id, imageId: imageRow.id };
};

export const generateHyperDuskSharePreview = async (
    projectId: string,
    options: {
        presetId: string;
        nanoPrompt: string;
        klingPromptBase: string;
        animationStyle: HyperDuskAnimationStyle;
        sceneType: string;
    }
): Promise<{ success: boolean; error?: string }> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) return { success: false, error: 'Not logged in' };

    const { data, error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
        body: { projectId, phase: 'dusk_preview', ...options },
        headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) return { success: false, error: error.message || 'Dusk preview failed' };
    if (data && typeof data === 'object' && 'error' in data && data.error) {
        return { success: false, error: String(data.error) };
    }
    return { success: true };
};

export const approveHyperDuskShareVariant = async (
    projectId: string,
    imageId: string,
    selectedVariantId?: string
): Promise<{ success: boolean; error?: string }> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) return { success: false, error: 'Not logged in' };

    const { data, error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
        body: { projectId, phase: 'approve', imageId, selectedVariantId },
        headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) return { success: false, error: error.message || 'Approve failed' };
    if (data && typeof data === 'object' && 'error' in data && data.error) {
        return { success: false, error: String(data.error) };
    }
    return { success: true };
};

export const generateHyperDuskShareAnimation = async (
    projectId: string,
    options: {
        durationSeconds: number;
        animationStyle: HyperDuskAnimationStyle;
        selectedVariantId?: string;
    }
): Promise<{ success: boolean; error?: string }> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) return { success: false, error: 'Not logged in' };

    const { error } = await supabase.functions.invoke('imagemotion-hyperdusk-generate', {
        body: { projectId, phase: 'animation', ...options },
        headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) return { success: false, error: error.message || 'Animation failed' };
    return { success: true };
};

export const pollHyperDuskShareStatus = async (
    projectId: string
): Promise<{ error?: string } & { results?: Array<{ imageId: string; status: string; done: boolean; error?: string }> }> => {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;
    if (!accessToken) return { error: 'Not logged in' };

    const { data, error } = await supabase.functions.invoke('imagemotion-hyperdusk-poll', {
        body: { projectId },
        headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (error) return { error: error.message || 'Poll failed' };
    return data as any;
};

export const fetchHyperDuskShareProject = async (
    projectId: string
): Promise<{ project: HyperDuskProjectLite | null; images: HyperDuskProjectImageLite[] }> => {
    if (!isConfigured) return { project: null, images: [] };

    const { data: projectRow, error: projectError } = await supabase
        .from('imagemotion_projects')
        .select('*')
        .eq('id', projectId)
        .eq('service_type', 'hyper_dusk')
        .single();
    if (projectError || !projectRow) return { project: null, images: [] };

    const { data: imageRows } = await supabase
        .from('imagemotion_project_images')
        .select('*')
        .eq('project_id', projectId)
        .order('order_index', { ascending: true });

    const project = projectRow as unknown as HyperDuskProjectLite;
    if (project.status === 'completed' && (project.final_video_path || project.final_video_url)) {
        const fresh = await getFreshHyperDuskUrl(project.final_video_path, project.final_video_url);
        if (fresh) project.final_video_url = fresh;
    }

    const images = ((imageRows ?? []) as unknown as HyperDuskProjectImageLite[]);
    await Promise.all(
        images.map(async (img) => {
            const fresh = await getFreshHyperDuskUrl(img.image_path ?? null, img.image_url ?? null);
            if (fresh) img.image_url = fresh;
            if (img.dusk_image_path || img.dusk_image_url) {
                const freshDusk = await getFreshHyperDuskUrl(img.dusk_image_path ?? null, img.dusk_image_url ?? null);
                if (freshDusk) img.dusk_image_url = freshDusk;
            }
            if (img.clip_url) {
                const freshClip = await getFreshHyperDuskUrl(null, img.clip_url);
                if (freshClip) img.clip_url = freshClip;
            }
            const variants = normalizeHyperDuskVariants((img as any).dusk_variants);
            if (variants.length) {
                const refreshed = await Promise.all(
                    variants.map(async (variant) => {
                        const variantUrl = await getFreshHyperDuskUrl(variant.path, variant.url ?? null);
                        return { ...variant, url: variantUrl ?? variant.url ?? null };
                    })
                );
                img.dusk_variants = refreshed;
            } else {
                img.dusk_variants = [];
            }
        })
    );

    return { project, images };
};

/**
 * Re-host a Hyper Dusk asset (still or video) into the public `images`
 * bucket so the share-demo public page can render it without short-lived
 * signed URLs. Returns the public URL or null on failure.
 */
export const copyHyperDuskAssetToShareDemo = async (
    sourceUrl: string,
    demoId: string,
    kind: 'processed_image' | 'processed_video'
): Promise<string | null> => {
    if (!isConfigured) return null;
    try {
        const response = await fetch(sourceUrl);
        if (!response.ok) throw new Error(`fetch failed (${response.status})`);
        const blob = await response.blob();
        const ext = kind === 'processed_video' ? 'mp4' : (blob.type.includes('png') ? 'png' : 'jpg');
        const filename = `share-demos/${demoId}/${kind}.${ext}`;

        const { error } = await supabase.storage
            .from('images')
            .upload(filename, blob, {
                cacheControl: '31536000',
                upsert: true,
                contentType: blob.type || (kind === 'processed_video' ? 'video/mp4' : 'image/jpeg'),
            });
        if (error) {
            console.error('copyHyperDuskAssetToShareDemo upload error:', error);
            return null;
        }
        const { data: { publicUrl } } = supabase.storage.from('images').getPublicUrl(filename);
        return publicUrl;
    } catch (err) {
        console.error('copyHyperDuskAssetToShareDemo failed:', err);
        return null;
    }
};

export { sleep as _hyperDuskSleep };

export { isConfigured };
